import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Search, Plus, ChevronRight, ChevronDown, Folder, FileText,
  ThumbsUp, ThumbsDown, Edit2, Trash2, ArrowLeft, Save, X,
  Lock, Globe,
  Bold, Italic, Underline, List, ListOrdered,
  Heading1, Heading2, Image as ImageIcon, Link as LinkIcon, Eraser, Type,
} from 'lucide-react';
import { useApi } from '../auth/useApi';
import { DocumentViewer } from '../components/DocumentViewer';

const C = {
  primary: 'var(--c-primary)',
  secondary: 'var(--c-secondary)',
  bg: 'var(--c-bg)',
  surface: 'var(--c-surface)',
  border: 'var(--c-border)',
  text: 'var(--c-text)',
  textMuted: 'var(--c-textMuted)',
  ok: 'var(--c-ok)',
  warn: 'var(--c-warn)',
  danger: 'var(--c-danger)',
};

interface Me {
  userId: number;
  email: string;
  displayName: string;
  role: 'admin' | 'tech' | 'user' | 'viewer' | 'agent';
}

interface Folder {
  FolderId: number;
  Name: string;
  Icon: string | null;
  ParentFolderId: number | null;
  SortOrder: number;
  Visibility: 'public' | 'internal';
}

interface Article {
  ArticleId: number;
  Title: string;
  Body: string;
  Category: string | null;
  FolderId: number | null;
  Tags: string | null;
  HelpfulCount: number;
  NotHelpfulCount: number;
  UsageCount: number;
  AuthorUserId: number | null;
  AuthorName?: string;
  CreatedAt: string;
  UpdatedAt: string;
  Visibility: 'public' | 'internal';
  ContentType?: 'text' | 'pdf';
  BlobName?: string | null;
  FileName?: string | null;
  SizeBytes?: number | null;
}

// -------- UI primitives --------
const Btn: React.FC<{
  children: React.ReactNode;
  onClick?: () => void;
  variant?: 'primary' | 'ghost' | 'danger';
  size?: 'sm' | 'md';
  disabled?: boolean;
  style?: React.CSSProperties;
}> = ({ children, onClick, variant = 'primary', size = 'md', disabled, style }) => {
  const palette = {
    primary: { bg: C.primary, fg: '#fff', border: C.primary },
    ghost: { bg: 'transparent', fg: C.text, border: C.border },
    danger: { bg: C.danger, fg: '#fff', border: C.danger },
  }[variant];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      background: disabled ? '#ccc' : palette.bg, color: palette.fg, border: `1px solid ${palette.border}`,
      padding: size === 'sm' ? '4px 10px' : '8px 14px', borderRadius: 6,
      fontSize: size === 'sm' ? 12 : 13, fontWeight: 500, cursor: disabled ? 'not-allowed' : 'pointer',
      opacity: disabled ? 0.6 : 1, display: 'inline-flex', alignItems: 'center', gap: 6, ...style,
    }}>{children}</button>
  );
};

const fmtRelative = (iso: string): string => {
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(iso).toLocaleDateString();
};

// ============================================================================
// KB Page
// ============================================================================
export const KnowledgeBase: React.FC<{ me: Me }> = ({ me }) => {
  const api = useApi();
  const [folders, setFolders] = useState<Folder[]>([]);
  const [articles, setArticles] = useState<Article[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI state
  const [selectedFolder, setSelectedFolder] = useState<number | null>(null);
  const [selectedArticleId, setSelectedArticleId] = useState<number | null>(null);
  const [editingArticle, setEditingArticle] = useState<Article | null>(null);
  const [creatingArticle, setCreatingArticle] = useState(false);
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [expanded, setExpanded] = useState<Set<number>>(new Set());
  const [search, setSearch] = useState('');
  const [visibilityFilter, setVisibilityFilter] = useState<'all' | 'public' | 'internal'>('all');

  const canEdit = me.role === 'admin' || me.role === 'tech';
  const canDelete = me.role === 'admin';

  const load = async () => {
    setLoading(true);
    try {
      const [f, a] = await Promise.all([
        api.get<Folder[]>('/api/kb/folders'),
        api.get<Article[]>('/api/kb/articles'),
      ]);
      setFolders(Array.isArray(f) ? f : []);
      setArticles(Array.isArray(a) ? a : []);
      setError(null);
    } catch (e: any) { setError(e?.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Deep-link: open a specific article when arriving at /kb?article=<id> (e.g. from a ticket)
  const deepLinkDone = useRef(false);
  useEffect(() => {
    if (deepLinkDone.current || !articles.length) return;
    const aid = Number(new URLSearchParams(window.location.search).get('article'));
    if (aid && articles.some(a => a.ArticleId === aid)) {
      setSelectedArticleId(aid);
      deepLinkDone.current = true;
    }
  }, [articles]);

  // Auto-expand root folders on first load
  useEffect(() => {
    if (folders.length && expanded.size === 0) {
      const roots = folders.filter(f => f.ParentFolderId === null).map(f => f.FolderId);
      setExpanded(new Set(roots));
    }
  // eslint-disable-next-line
  }, [folders]);

  const selectedArticle = useMemo(
    () => articles.find(a => a.ArticleId === selectedArticleId) ?? null,
    [articles, selectedArticleId]
  );

  // Search filtering — when searching, ignore folder selection
  const visibleArticles = useMemo(() => {
    // First apply visibility filter (techs/admins only; users always see only public from server)
    const visFiltered = visibilityFilter === 'all'
      ? articles
      : articles.filter(a => a.Visibility === visibilityFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      return visFiltered.filter(a =>
        a.Title.toLowerCase().includes(s) ||
        stripHtml(a.Body).toLowerCase().includes(s) ||
        (a.Tags ?? '').toLowerCase().includes(s) ||
        (a.Category ?? '').toLowerCase().includes(s)
      );
    }
    if (selectedFolder === null) return visFiltered.filter(a => a.FolderId === null);
    return visFiltered.filter(a => a.FolderId === selectedFolder);
  }, [articles, search, selectedFolder, visibilityFilter]);

  const handleVote = async (id: number, helpful: boolean) => {
    try {
      await api.post(`/api/kb/articles/${id}/feedback`, { helpful });
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const handleDeleteArticle = async (id: number, title: string) => {
    if (!window.confirm(`Delete article "${title}"? This cannot be undone.`)) return;
    try {
      await api.del(`/api/kb/articles/${id}`);
      if (selectedArticleId === id) setSelectedArticleId(null);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const handleRenameFolder = async (id: number, currentName: string) => {
    const name = window.prompt('Rename folder:', currentName);
    if (name === null) return;
    const trimmed = name.trim();
    if (!trimmed || trimmed === currentName) return;
    try { await api.patch(`/api/kb/folders/${id}`, { name: trimmed }); await load(); }
    catch (e: any) { alert(`Rename failed: ${e?.message ?? 'error'}`); }
  };

  const handleMoveArticle = async (id: number, dir: 'up' | 'down') => {
    const inFolder = articles.filter(a => a.FolderId === selectedFolder);
    const idx = inFolder.findIndex(a => a.ArticleId === id);
    if (idx < 0) return;
    const swap = dir === 'up' ? idx - 1 : idx + 1;
    if (swap < 0 || swap >= inFolder.length) return;
    const ids = inFolder.map(a => a.ArticleId);
    [ids[idx], ids[swap]] = [ids[swap], ids[idx]];
    try { await api.put('/api/kb/articles/reorder', { ids }); await load(); }
    catch (e: any) { alert(`Reorder failed: ${e?.message ?? 'error'}`); }
  };

  const handleDeleteFolder = async (id: number, name: string) => {
    if (!window.confirm(`Delete folder "${name}"? Articles inside will be unfiled.`)) return;
    try {
      await api.del(`/api/kb/folders/${id}`);
      if (selectedFolder === id) setSelectedFolder(null);
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  const handleToggleFolderVisibility = async (id: number, next: 'public' | 'internal') => {
    if (next === 'public') {
      const folder = folders.find(f => f.FolderId === id);
      const ok = window.confirm(
        `Make "${folder?.Name ?? 'this folder'}" PUBLIC?\n\n` +
        `Everyone — including end users/requesters — will be able to see this folder ` +
        `and the articles inside it. Only do this if none of its contents are staff-only.`
      );
      if (!ok) return;
    }
    try {
      await api.patch(`/api/kb/folders/${id}`, { visibility: next });
      await load();
    } catch (e: any) { alert(`Failed: ${e?.message}`); }
  };

  if (loading) return <div style={{ padding: 40, color: C.textMuted }}>Loading knowledge base…</div>;
  if (error) return <div style={{ padding: 40, color: C.danger }}>Failed to load: {error}</div>;

  return (
    <div>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', marginBottom: 20 }}>
        <div>
          <h1 style={{ color: C.primary, fontSize: 26, margin: 0 }}>Knowledge Base</h1>
          <p style={{ color: C.textMuted, margin: '4px 0 0' }}>{articles.length} articles · {folders.length} folders</p>
        </div>
        {canEdit && (
          <div style={{ display: 'flex', gap: 8 }}>
            <Btn variant="ghost" onClick={() => setCreatingFolder(true)}>
              <Folder size={14} /> New Folder
            </Btn>
            <Btn onClick={() => setCreatingArticle(true)}>
              <Plus size={14} /> New Article
            </Btn>
          </div>
        )}
      </div>

      {/* Visibility filter — techs and admins only */}
      {canEdit && (
        <div style={{ display: 'flex', gap: 6, marginBottom: 12 }}>
          {([
            { key: 'all', label: 'All', count: articles.length },
            { key: 'public', label: '🌐 Public', count: articles.filter(a => a.Visibility === 'public').length },
            { key: 'internal', label: '🔒 Internal', count: articles.filter(a => a.Visibility === 'internal').length },
          ] as const).map(opt => (
            <button
              key={opt.key}
              onClick={() => setVisibilityFilter(opt.key)}
              style={{
                background: visibilityFilter === opt.key ? C.primary : C.surface,
                color: visibilityFilter === opt.key ? '#fff' : C.text,
                border: `1px solid ${visibilityFilter === opt.key ? C.primary : C.border}`,
                padding: '6px 14px', borderRadius: 16, fontSize: 12,
                cursor: 'pointer', fontWeight: 500,
              }}
            >
              {opt.label} <span style={{
                marginLeft: 4, opacity: 0.7, fontSize: 11,
              }}>({opt.count})</span>
            </button>
          ))}
        </div>
      )}

      {/* Search */}
      <div style={{ position: 'relative', marginBottom: 16 }}>
        <Search size={14} style={{ position: 'absolute', left: 12, top: 11, color: C.textMuted }} />
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="Search articles by title, body, tags, or category…"
          style={{
            border: `1px solid ${C.border}`, padding: '8px 12px 8px 34px',
            borderRadius: 8, fontSize: 14, width: '100%', boxSizing: 'border-box',
            background: C.surface,
          }}
        />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 16 }}>
        {/* Folder tree */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 12 }}>
          {/* Note: canEdit here is admin-only on purpose — within the tree this flag
              gates ONLY the public/private toggle, and exposing an existing folder is
              an admin action. Create/delete buttons elsewhere use the broader canEdit. */}
          <FolderTree
            folders={folders}
            articles={articles}
            expanded={expanded}
            setExpanded={setExpanded}
            selectedFolder={selectedFolder}
            setSelectedFolder={(id) => { setSelectedFolder(id); setSelectedArticleId(null); setSearch(''); }}
            canDelete={canDelete}
            canEdit={me.role === 'admin'}
            onDelete={handleDeleteFolder}
            onToggleVisibility={handleToggleFolderVisibility}
            onRename={handleRenameFolder}
            canRename={canEdit}
          />
        </div>

        {/* Right column: article viewer or article list */}
        <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: 20, minHeight: 400 }}>
          {selectedArticle ? (
            <ArticleViewer
              article={selectedArticle}
              folder={folders.find(f => f.FolderId === selectedArticle.FolderId) ?? null}
              canEdit={canEdit}
              canDelete={canDelete}
              onBack={() => setSelectedArticleId(null)}
              onEdit={() => setEditingArticle(selectedArticle)}
              onDelete={() => handleDeleteArticle(selectedArticle.ArticleId, selectedArticle.Title)}
              onVote={handleVote}
            />
          ) : (
            <ArticleList
              articles={visibleArticles}
              search={search}
              folderName={
                search.trim() ? null :
                selectedFolder === null ? 'Unfiled' :
                folders.find(f => f.FolderId === selectedFolder)?.Name ?? null
              }
              onSelect={setSelectedArticleId}
              canEdit={canEdit}
              onMove={handleMoveArticle}
            />
          )}
        </div>
      </div>

      {/* Modals */}
      {(creatingArticle || editingArticle) && (
        <ArticleModal
          article={editingArticle}
          folders={folders}
          defaultFolderId={selectedFolder}
          onSave={async (data) => {
            try {
              if (data && data.__uploaded) {
                // PDF document was uploaded directly by the modal; just refresh.
                setCreatingArticle(false); setEditingArticle(null);
                await load();
                return;
              }
              if (editingArticle) {
                await api.patch(`/api/kb/articles/${editingArticle.ArticleId}`, data);
              } else {
                await api.post('/api/kb/articles', data);
              }
              setCreatingArticle(false); setEditingArticle(null);
              await load();
            } catch (e: any) { alert(`Failed: ${e?.message}`); }
          }}
          onClose={() => { setCreatingArticle(false); setEditingArticle(null); }}
        />
      )}

      {creatingFolder && (
        <FolderModal
          folders={folders}
          onSave={async (data) => {
            try {
              await api.post('/api/kb/folders', data);
              setCreatingFolder(false);
              await load();
            } catch (e: any) { alert(`Failed: ${e?.message}`); }
          }}
          onClose={() => setCreatingFolder(false)}
        />
      )}
    </div>
  );
};

// ============================================================================
// Folder Tree
// ============================================================================
const FolderTree: React.FC<{
  folders: Folder[];
  articles: Article[];
  expanded: Set<number>;
  setExpanded: (s: Set<number>) => void;
  selectedFolder: number | null;
  setSelectedFolder: (id: number | null) => void;
  canDelete: boolean;
  canEdit: boolean;
  onDelete: (id: number, name: string) => void;
  onToggleVisibility: (id: number, next: 'public' | 'internal') => void;
  onRename: (id: number, currentName: string) => void;
  canRename: boolean;
}> = ({ folders, articles, expanded, setExpanded, selectedFolder, setSelectedFolder, canDelete, canEdit, onDelete, onToggleVisibility, onRename, canRename }) => {
  const roots = folders.filter(f => f.ParentFolderId === null).sort((a, b) => a.SortOrder - b.SortOrder);
  const unfiled = articles.filter(a => a.FolderId === null);

  return (
    <div>
      {roots.map(f => (
        <FolderNode
          key={f.FolderId}
          folder={f}
          folders={folders}
          articles={articles}
          depth={0}
          onRename={onRename}
          canRename={canRename}
          expanded={expanded}
          setExpanded={setExpanded}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          canDelete={canDelete}
          canEdit={canEdit}
          onDelete={onDelete}
          onToggleVisibility={onToggleVisibility}
        />
      ))}
      {unfiled.length > 0 && (
        <div
          onClick={() => setSelectedFolder(null)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '6px 8px', borderRadius: 4, cursor: 'pointer', fontSize: 13,
            background: selectedFolder === null ? `${C.primary}15` : 'transparent',
            color: selectedFolder === null ? C.primary : C.text,
            fontWeight: selectedFolder === null ? 600 : 400,
            marginTop: 8, borderTop: `1px solid ${C.border}`,
          }}
        >
          <FileText size={14} />
          Unfiled
          <span style={{ marginLeft: 'auto', fontSize: 11, color: C.textMuted }}>{unfiled.length}</span>
        </div>
      )}
    </div>
  );
};

const FolderNode: React.FC<{
  folder: Folder;
  folders: Folder[];
  articles: Article[];
  depth: number;
  expanded: Set<number>;
  setExpanded: (s: Set<number>) => void;
  selectedFolder: number | null;
  setSelectedFolder: (id: number | null) => void;
  canDelete: boolean;
  canEdit: boolean;
  onDelete: (id: number, name: string) => void;
  onToggleVisibility: (id: number, next: 'public' | 'internal') => void;
  onRename: (id: number, currentName: string) => void;
  canRename: boolean;
}> = ({ folder, folders, articles, depth, expanded, setExpanded, selectedFolder, setSelectedFolder, canDelete, canEdit, onDelete, onToggleVisibility, onRename, canRename }) => {
  const isOpen = expanded.has(folder.FolderId);
  const children = folders.filter(f => f.ParentFolderId === folder.FolderId).sort((a, b) => a.SortOrder - b.SortOrder);
  const articleCount = articles.filter(a => a.FolderId === folder.FolderId).length;
  const isSelected = selectedFolder === folder.FolderId;
  const hasChildren = children.length > 0;

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const next = new Set(expanded);
    if (isOpen) next.delete(folder.FolderId); else next.add(folder.FolderId);
    setExpanded(next);
  };

  return (
    <div>
      <div
        onClick={() => setSelectedFolder(folder.FolderId)}
        style={{
          display: 'flex', alignItems: 'center', gap: 4,
          padding: '6px 8px', paddingLeft: 8 + depth * 16,
          borderRadius: 4, cursor: 'pointer', fontSize: 13,
          background: isSelected ? `${C.primary}15` : 'transparent',
          color: isSelected ? C.primary : C.text,
          fontWeight: isSelected ? 600 : 400,
        }}
      >
        <span onClick={toggle} style={{ display: 'inline-flex', width: 14 }}>
          {hasChildren ? (isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />) : null}
        </span>
        <span style={{ fontSize: 14 }}>{folder.Icon ?? '📁'}</span>
        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {folder.Name}
        </span>
        {articleCount > 0 && (
          <span style={{ fontSize: 11, color: C.textMuted }}>{articleCount}</span>
        )}
        {/* Visibility: lock = private (staff only), globe = public. Click to flip (staff). */}
        {canEdit ? (
          <button
            onClick={(e) => { e.stopPropagation(); onToggleVisibility(folder.FolderId, folder.Visibility === 'public' ? 'internal' : 'public'); }}
            title={folder.Visibility === 'internal' ? 'Private — staff only. Click to make public.' : 'Public. Click to make private.'}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2,
              color: folder.Visibility === 'internal' ? C.warn : C.textMuted, display: 'inline-flex' }}
          >
            {folder.Visibility === 'internal' ? <Lock size={11} /> : <Globe size={11} />}
          </button>
        ) : folder.Visibility === 'internal' ? (
          <Lock size={11} style={{ color: C.warn }} />
        ) : null}
        {canRename && isSelected && (
          <button
            onClick={(e) => { e.stopPropagation(); onRename(folder.FolderId, folder.Name); }}
            title="Rename folder"
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 2, color: C.textMuted, display: 'inline-flex' }}
          >
            <Edit2 size={11} />
          </button>
        )}
        {canDelete && isSelected && (
          <button
            onClick={(e) => { e.stopPropagation(); onDelete(folder.FolderId, folder.Name); }}
            style={{
              background: 'none', border: 'none', cursor: 'pointer',
              padding: 2, color: C.danger, display: 'inline-flex',
            }}
          >
            <Trash2 size={11} />
          </button>
        )}
      </div>
      {isOpen && children.map(c => (
        <FolderNode
          key={c.FolderId}
          folder={c}
          folders={folders}
          articles={articles}
          depth={depth + 1}
          onRename={onRename}
          canRename={canRename}
          expanded={expanded}
          setExpanded={setExpanded}
          selectedFolder={selectedFolder}
          setSelectedFolder={setSelectedFolder}
          canDelete={canDelete}
          canEdit={canEdit}
          onDelete={onDelete}
          onToggleVisibility={onToggleVisibility}
        />
      ))}
    </div>
  );
};

// ============================================================================
// Article List
// ============================================================================
const ArticleList: React.FC<{
  articles: Article[];
  search: string;
  folderName: string | null;
  onSelect: (id: number) => void;
  canEdit: boolean;
  onMove: (id: number, dir: 'up' | 'down') => void;
}> = ({ articles, search, folderName, onSelect, canEdit, onMove }) => {
  if (articles.length === 0) {
    return (
      <div style={{ padding: 60, textAlign: 'center', color: C.textMuted }}>
        {search ? `No articles match "${search}"` : 'No articles in this folder.'}
      </div>
    );
  }
  return (
    <div>
      <h2 style={{ color: C.primary, fontSize: 18, margin: '0 0 16px' }}>
        {search ? `Search results for "${search}"` : folderName ?? 'Articles'}
        <span style={{ fontSize: 13, color: C.textMuted, fontWeight: 400, marginLeft: 8 }}>
          {articles.length} {articles.length === 1 ? 'article' : 'articles'}
        </span>
      </h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {articles.map((a, i) => (
          <div
            key={a.ArticleId}
            onClick={() => onSelect(a.ArticleId)}
            style={{
              padding: 14, border: `1px solid ${C.border}`, borderRadius: 6,
              cursor: 'pointer', background: C.bg,
              transition: 'background 0.15s',
            }}
            onMouseEnter={e => (e.currentTarget.style.background = '#f0ebe0')}
            onMouseLeave={e => (e.currentTarget.style.background = C.bg)}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                  {a.Visibility === 'internal' && (
                    <span style={{
                      fontSize: 10, padding: '1px 6px', borderRadius: 3,
                      background: '#fff3e0', color: '#7a4e15', fontWeight: 600,
                      border: '1px solid #e6c97a', textTransform: 'uppercase', letterSpacing: '0.4px',
                    }}>🔒 Internal</span>
                  )}
                  <span>{a.Title}</span>
                </div>
                <div style={{
                  fontSize: 13, color: C.textMuted, lineHeight: 1.4,
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                }}>
                  {stripHtml(a.Body).slice(0, 200)}{stripHtml(a.Body).length > 200 ? '…' : ''}
                </div>
                {a.Tags && (
                  <div style={{ marginTop: 6, display: 'flex', flexWrap: 'wrap', gap: 4 }}>
                    {a.Tags.split(',').slice(0, 4).map((t, i) => (
                      <span key={i} style={{
                        fontSize: 10, padding: '1px 6px',
                        background: `${C.secondary}20`, color: C.secondary,
                        borderRadius: 3, fontWeight: 500,
                      }}>{t.trim()}</span>
                    ))}
                  </div>
                )}
              </div>
              <div style={{ fontSize: 11, color: C.textMuted, textAlign: 'right', whiteSpace: 'nowrap' }}>
                <div>👍 {a.HelpfulCount} · 👎 {a.NotHelpfulCount}</div>
                <div style={{ marginTop: 2 }}>{fmtRelative(a.UpdatedAt)}</div>
              </div>
              {canEdit && !search && (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }} onClick={e => e.stopPropagation()}>
                  <button onClick={() => onMove(a.ArticleId, 'up')} disabled={i === 0} title="Move up"
                    style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 3, color: C.textMuted, padding: '0 5px', cursor: i === 0 ? 'not-allowed' : 'pointer', opacity: i === 0 ? 0.4 : 1 }}>▲</button>
                  <button onClick={() => onMove(a.ArticleId, 'down')} disabled={i === articles.length - 1} title="Move down"
                    style={{ background: 'none', border: `1px solid ${C.border}`, borderRadius: 3, color: C.textMuted, padding: '0 5px', cursor: i === articles.length - 1 ? 'not-allowed' : 'pointer', opacity: i === articles.length - 1 ? 0.4 : 1 }}>▼</button>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// Article Viewer
// ============================================================================
const ArticleViewer: React.FC<{
  article: Article;
  folder: Folder | null;
  canEdit: boolean;
  canDelete: boolean;
  onBack: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onVote: (id: number, helpful: boolean) => void;
}> = ({ article, folder, canEdit, canDelete, onBack, onEdit, onDelete, onVote }) => (
  <div>
    <button
      onClick={onBack}
      style={{
        background: 'none', border: 'none', color: C.primary,
        fontSize: 13, cursor: 'pointer', display: 'inline-flex',
        alignItems: 'center', gap: 4, padding: 0, marginBottom: 12,
      }}
    >
      <ArrowLeft size={14} /> Back to list
    </button>

    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 16, marginBottom: 8 }}>
      <div style={{ flex: 1 }}>
        {article.Visibility === 'internal' && (
          <div style={{
            display: 'inline-block', fontSize: 11, padding: '2px 10px', borderRadius: 3,
            background: '#fff3e0', color: '#7a4e15', fontWeight: 600,
            border: '1px solid #e6c97a', textTransform: 'uppercase', letterSpacing: '0.4px',
            marginBottom: 6,
          }}>🔒 Internal — only techs & admins see this</div>
        )}
        <h1 style={{ color: C.text, fontSize: 24, margin: 0, fontWeight: 600 }}>{article.Title}</h1>
      </div>
      {canEdit && (
        <div style={{ display: 'flex', gap: 6 }}>
          <Btn variant="ghost" size="sm" onClick={onEdit}><Edit2 size={12} /> Edit</Btn>
          {canDelete && <Btn variant="ghost" size="sm" onClick={onDelete}><Trash2 size={12} /></Btn>}
        </div>
      )}
    </div>

    <div style={{ fontSize: 12, color: C.textMuted, marginBottom: 18, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
      {folder && <span>📁 {folder.Name}</span>}
      {article.Category && <span>· {article.Category}</span>}
      <span>· Updated {fmtRelative(article.UpdatedAt)}</span>
      {article.AuthorName && <span>· by {article.AuthorName}</span>}
    </div>

    {article.Tags && (
      <div style={{ marginBottom: 18, display: 'flex', flexWrap: 'wrap', gap: 6 }}>
        {article.Tags.split(',').map((t, i) => (
          <span key={i} style={{
            fontSize: 11, padding: '2px 8px',
            background: `${C.secondary}20`, color: C.secondary,
            borderRadius: 3, fontWeight: 500,
          }}>{t.trim()}</span>
        ))}
      </div>
    )}

    {article.ContentType === 'pdf' ? (
      <PdfArticleBody articleId={article.ArticleId} fileName={article.FileName} />
    ) : (
      <ArticleBody html={article.Body} />
    )}

    <div style={{
      marginTop: 24, padding: 16, background: C.bg, borderRadius: 8,
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    }}>
      <div style={{ fontSize: 13, color: C.textMuted }}>Was this article helpful?</div>
      <div style={{ display: 'flex', gap: 8 }}>
        <Btn variant="ghost" size="sm" onClick={() => onVote(article.ArticleId, true)}>
          <ThumbsUp size={12} /> Yes ({article.HelpfulCount})
        </Btn>
        <Btn variant="ghost" size="sm" onClick={() => onVote(article.ArticleId, false)}>
          <ThumbsDown size={12} /> No ({article.NotHelpfulCount})
        </Btn>
      </div>
    </div>
  </div>
);

// ============================================================================
// Article Modal (create/edit)
// ============================================================================
// PDF document body — fetches a fresh inline-view SAS and renders via DocumentViewer.
const PdfArticleBody: React.FC<{ articleId: number; fileName?: string | null }> = ({ articleId, fileName }) => {
  const api = useApi();
  const getUrl = React.useCallback(async () => {
    const r = await api.get<{ url: string; fileName: string | null }>(`/api/kb/articles/${articleId}/view-sas`);
    return r.url;
  }, [api, articleId]);
  return (
    <div style={{ padding: '20px 0', borderTop: `1px solid ${C.border}` }}>
      <DocumentViewer getUrl={getUrl} kind="pdf" fileName={fileName ?? 'Document'} />
    </div>
  );
};

const ArticleModal: React.FC<{
  article: Article | null;
  folders: Folder[];
  defaultFolderId: number | null;
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
}> = ({ article, folders, defaultFolderId, onSave, onClose }) => {
  const api = useApi();
  // Existing PDF articles can't switch type; only NEW articles offer the choice.
  const isExistingPdf = article?.ContentType === 'pdf';
  const [mode, setMode] = useState<'text' | 'pdf'>(isExistingPdf ? 'pdf' : 'text');
  const [pdfFile, setPdfFile] = useState<File | null>(null);
  const pdfInputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState(article?.Title ?? '');
  const [body, setBody] = useState(article?.Body ?? '');
  const [category, setCategory] = useState(article?.Category ?? '');
  const [folderId, setFolderId] = useState<number | null>(article?.FolderId ?? defaultFolderId);
  const [tags, setTags] = useState(article?.Tags ?? '');
  // Default new articles to 'internal' — techs must opt-in to promote
  const [visibility, setVisibility] = useState<'public' | 'internal'>(article?.Visibility ?? 'internal');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!title.trim()) { alert('Title is required'); return; }
    if (mode === 'pdf') {
      // New PDF document article: multipart upload to the dedicated endpoint.
      if (!article && !pdfFile) { alert('Please choose a PDF file'); return; }
      setSaving(true);
      try {
        if (pdfFile) {
          const fd = new FormData();
          fd.append('file', pdfFile);
          fd.append('title', title.trim());
          if (category.trim()) fd.append('category', category.trim());
          if (folderId != null) fd.append('folderId', String(folderId));
          if (tags.trim()) fd.append('tags', tags.trim());
          fd.append('visibility', visibility);
          const token = await api.getAccessToken();
          const res = await fetch(`${api.apiBase.replace(/\/$/, '')}/api/kb/articles/upload`, {
            method: 'POST',
            headers: { Authorization: `Bearer ${token}` },
            body: fd,
          });
          if (!res.ok) {
            const detail = await res.text().catch(() => '');
            throw new Error(`Upload failed (${res.status}): ${detail.slice(0, 200)}`);
          }
        }
        // Signal the parent to refresh/close without going through the JSON onSave path.
        await onSave({ __uploaded: true });
      } catch (e: any) {
        alert(e?.message ?? 'PDF upload failed');
      } finally { setSaving(false); }
      return;
    }
    if (!stripHtml(body).trim()) { alert('Title and body are required'); return; }
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        body: body,  // keep HTML intact, don't trim
        category: category.trim() || null,
        folderId: folderId,
        tags: tags.trim() || null,
        visibility: visibility,
      });
    } finally { setSaving(false); }
  };

  return (
    <Modal title={article ? 'Edit Article' : 'New Article'} onClose={onClose} width={720}>
      {!article && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
          <button type="button" onClick={() => setMode('text')}
            style={{ flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: mode === 'text' ? C.primary : 'transparent', color: mode === 'text' ? '#fff' : C.text,
              border: `1px solid ${mode === 'text' ? C.primary : C.border}` }}>
            Write article
          </button>
          <button type="button" onClick={() => setMode('pdf')}
            style={{ flex: 1, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer',
              background: mode === 'pdf' ? C.primary : 'transparent', color: mode === 'pdf' ? '#fff' : C.text,
              border: `1px solid ${mode === 'pdf' ? C.primary : C.border}` }}>
            Upload PDF
          </button>
        </div>
      )}
      <Field label="Title">
        <input value={title} onChange={e => setTitle(e.target.value)}
          style={inputSty()} placeholder="Short, searchable title" />
      </Field>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
        <Field label="Folder">
          <select value={folderId ?? ''} onChange={e => setFolderId(e.target.value ? parseInt(e.target.value) : null)} style={inputSty()}>
            <option value="">(unfiled)</option>
            {folders.map(f => <option key={f.FolderId} value={f.FolderId}>{f.Name}</option>)}
          </select>
        </Field>
        <Field label="Category">
          <input value={category} onChange={e => setCategory(e.target.value)}
            style={inputSty()} placeholder="e.g. VPN, Email" />
        </Field>
      </div>

      <Field label="Tags (comma-separated)">
        <input value={tags} onChange={e => setTags(e.target.value)}
          style={inputSty()} placeholder="vpn, networking, troubleshooting" />
      </Field>

      <Field label="Visibility">
        <div style={{
          display: 'flex', gap: 8, padding: 12, background: C.bg, borderRadius: 6,
          border: `1px solid ${C.border}`,
        }}>
          <label style={{
            flex: 1, display: 'flex', alignItems: 'flex-start', gap: 8, padding: 10,
            background: visibility === 'internal' ? C.surface : 'transparent',
            border: `1px solid ${visibility === 'internal' ? C.primary : 'transparent'}`,
            borderRadius: 4, cursor: 'pointer',
          }}>
            <input
              type="radio" name="vis" checked={visibility === 'internal'}
              onChange={() => setVisibility('internal')} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>🔒 Internal</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, lineHeight: 1.4 }}>
                Only admins and techs can see this. Use for runbooks, vendor contacts, account specifics.
              </div>
            </div>
          </label>
          <label style={{
            flex: 1, display: 'flex', alignItems: 'flex-start', gap: 8, padding: 10,
            background: visibility === 'public' ? C.surface : 'transparent',
            border: `1px solid ${visibility === 'public' ? C.primary : 'transparent'}`,
            borderRadius: 4, cursor: 'pointer',
          }}>
            <input
              type="radio" name="vis" checked={visibility === 'public'}
              onChange={() => setVisibility('public')} style={{ marginTop: 2 }} />
            <div>
              <div style={{ fontSize: 13, fontWeight: 600, color: C.text }}>🌐 Public</div>
              <div style={{ fontSize: 11, color: C.textMuted, marginTop: 2, lineHeight: 1.4 }}>
                Every employee can read this. Use for how-tos, FAQs, self-service.
              </div>
            </div>
          </label>
        </div>
      </Field>

      {mode === 'pdf' ? (
        <Field label="PDF document">
          <div style={{ border: `1px dashed ${C.border}`, borderRadius: 6, padding: 18, textAlign: 'center', background: C.bg }}>
            <input ref={pdfInputRef} type="file" accept="application/pdf,.pdf" style={{ display: 'none' }}
              onChange={e => setPdfFile(e.target.files?.[0] ?? null)} />
            {isExistingPdf && !pdfFile ? (
              <div style={{ fontSize: 13, color: C.textMuted }}>
                This article is an uploaded PDF. Replacing the file isn't supported here — delete and re-upload to change it.
              </div>
            ) : (
              <>
                <button type="button" onClick={() => pdfInputRef.current?.click()}
                  style={{ background: C.primary, color: '#fff', border: 'none', borderRadius: 6, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                  {pdfFile ? 'Choose a different file' : 'Choose PDF file'}
                </button>
                <div style={{ marginTop: 10, fontSize: 13, color: pdfFile ? C.text : C.textMuted }}>
                  {pdfFile ? pdfFile.name : 'No file chosen · PDF only, up to 50 MB'}
                </div>
              </>
            )}
          </div>
        </Field>
      ) : (
        <Field label="Body">
          <RichTextEditor
            defaultHtml={article?.Body ?? ''}
            onChange={setBody}
          />
        </Field>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving || !title.trim() || (mode === 'text' ? !stripHtml(body).trim() : (!article && !pdfFile))}>
          <Save size={12} /> {saving ? 'Saving…' : 'Save'}
        </Btn>
      </div>
    </Modal>
  );
};

// ============================================================================
// Folder Modal (create)
// ============================================================================
const FolderModal: React.FC<{
  folders: Folder[];
  onSave: (data: any) => Promise<void>;
  onClose: () => void;
}> = ({ folders, onSave, onClose }) => {
  const [name, setName] = useState('');
  const [icon, setIcon] = useState('📁');
  const [parentFolderId, setParentFolderId] = useState<number | null>(null);
  const [visibility, setVisibility] = useState<'public' | 'internal'>('public');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!name.trim()) { alert('Name is required'); return; }
    setSaving(true);
    try {
      await onSave({
        name: name.trim(),
        icon: icon || null,
        parentFolderId: parentFolderId,
        visibility,
      });
    } finally { setSaving(false); }
  };

  return (
    <Modal title="New Folder" onClose={onClose} width={460}>
      <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 12 }}>
        <Field label="Icon">
          <input value={icon} onChange={e => setIcon(e.target.value.slice(0, 4))}
            style={{ ...inputSty(), textAlign: 'center', fontSize: 18 }} maxLength={4} />
        </Field>
        <Field label="Name">
          <input value={name} onChange={e => setName(e.target.value)}
            style={inputSty()} placeholder="Folder name" autoFocus />
        </Field>
      </div>

      <Field label="Parent Folder (optional)">
        <select value={parentFolderId ?? ''} onChange={e => setParentFolderId(e.target.value ? parseInt(e.target.value) : null)} style={inputSty()}>
          <option value="">(top level)</option>
          {folders.map(f => <option key={f.FolderId} value={f.FolderId}>{f.Name}</option>)}
        </select>
      </Field>

      <Field label="Visibility">
        <select value={visibility} onChange={e => setVisibility(e.target.value as 'public' | 'internal')} style={inputSty()}>
          <option value="public">Public — visible to everyone, including requesters</option>
          <option value="internal">Private — staff only (admins &amp; techs)</option>
        </select>
      </Field>

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Btn variant="ghost" onClick={onClose}>Cancel</Btn>
        <Btn onClick={save} disabled={saving || !name.trim()}>
          <Save size={12} /> {saving ? 'Saving…' : 'Save'}
        </Btn>
      </div>
    </Modal>
  );
};

// ============================================================================
// Shared Modal + Field
// ============================================================================
const Modal: React.FC<{ title: string; onClose: () => void; width?: number; children: React.ReactNode }> = ({ title, onClose, width = 600, children }) => (
  <div style={{
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 100, padding: 20,
  }}>
    <div style={{
      background: C.surface, borderRadius: 8, width: '100%', maxWidth: width,
      maxHeight: '90vh', display: 'flex', flexDirection: 'column',
    }}>
      <div style={{
        padding: '14px 20px', borderBottom: `1px solid ${C.border}`,
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      }}>
        <h3 style={{ margin: 0, color: C.primary, fontSize: 16 }}>{title}</h3>
        <button onClick={onClose} style={{
          background: 'none', border: 'none', cursor: 'pointer',
          padding: 4, color: C.textMuted, display: 'inline-flex',
        }}><X size={16} /></button>
      </div>
      <div style={{ padding: 20, overflowY: 'auto', flex: 1 }}>{children}</div>
    </div>
  </div>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <div style={{ marginBottom: 14 }}>
    <div style={{
      fontSize: 11, fontWeight: 600, color: C.textMuted,
      marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.4px',
    }}>{label}</div>
    {children}
  </div>
);

const inputSty = (): React.CSSProperties => ({
  border: `1px solid ${C.border}`, padding: '8px 10px', borderRadius: 6,
  fontSize: 14, width: '100%', boxSizing: 'border-box', background: C.bg,
});

// ============================================================================
// Helpers — HTML rendering / stripping
// ============================================================================

/** Strip HTML tags to plain text (used for snippets, search, and "is empty" checks). */
function stripHtml(html: string | null | undefined): string {
  if (!html) return '';
  // If it doesn't look like HTML, return as-is
  if (!/<[a-z][^>]*>/i.test(html)) return html;
  const div = document.createElement('div');
  div.innerHTML = html;
  return div.textContent || div.innerText || '';
}

/** Render article body. Auto-detects plain text vs HTML for backwards compatibility. */
const ArticleBody: React.FC<{ html: string }> = ({ html }) => {
  const isHtml = /<[a-z][^>]*>/i.test(html ?? '');
  return (
    <div style={{
      lineHeight: 1.7, fontSize: 14, color: C.text,
      padding: '20px 0', borderTop: `1px solid ${C.border}`,
      ...(isHtml ? {} : { whiteSpace: 'pre-wrap' as const }),
    }}
      className="kb-article-body"
      // Plain-text bodies have no markup, so dangerouslySetInnerHTML is safe.
      // Rich-text bodies are produced by our editor (admin/tech only) and stored verbatim.
      dangerouslySetInnerHTML={isHtml ? { __html: html } : undefined}
    >
      {isHtml ? undefined : html}
    </div>
  );
};

// ============================================================================
// RichTextEditor — contentEditable + execCommand based.
// Toolbar: bold/italic/underline, headings, lists, color, link, image upload, clear.
// ============================================================================
const RichTextEditor: React.FC<{
  defaultHtml: string;
  onChange: (html: string) => void;
}> = ({ defaultHtml, onChange }) => {
  const api = useApi();
  const editorRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [showColorPicker, setShowColorPicker] = useState(false);

  // Initialize editor content once. We intentionally use defaultHtml (not value-bound) so
  // every keystroke doesn't cause React to wipe the editor; instead we forward HTML on input.
  useEffect(() => {
    if (editorRef.current && !editorRef.current.innerHTML) {
      // If the existing content looks like plain text, wrap each line in <p>
      if (defaultHtml && !/<[a-z][^>]*>/i.test(defaultHtml)) {
        editorRef.current.innerHTML = defaultHtml
          .split(/\r?\n/)
          .map(l => l.trim().length === 0 ? '<p><br></p>' : `<p>${escapeHtml(l)}</p>`)
          .join('');
      } else {
        editorRef.current.innerHTML = defaultHtml || '';
      }
      onChange(editorRef.current.innerHTML);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fire = () => {
    if (editorRef.current) onChange(editorRef.current.innerHTML);
  };

  // Apply a formatting command. We focus the editor first so the selection stays valid.
  const exec = (command: string, value?: string) => {
    editorRef.current?.focus();
    document.execCommand(command, false, value);
    fire();
  };

  // Image upload handler — POSTs to /api/kb/upload-image and inserts the URL.
  const handleImagePick = async (file: File) => {
    setUploadingImage(true);
    try {
      const form = new FormData();
      form.append('image', file);
      // Use raw fetch since useApi's helpers are JSON-focused. Bring auth token from msal.
      const token = await api.getAccessToken();
      const resp = await fetch(`${api.apiBase}/api/kb/upload-image`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` },
        body: form,
      });
      if (!resp.ok) {
        const txt = await resp.text();
        throw new Error(txt || `Upload failed (${resp.status})`);
      }
      const data = await resp.json();
      editorRef.current?.focus();
      // Insert at cursor as an <img>; cap width with style so very large screenshots don't blow out the layout
      document.execCommand(
        'insertHTML', false,
        `<img src="${escapeAttr(data.url)}" alt="screenshot" style="max-width:100%;height:auto;border-radius:4px;margin:8px 0;" />`
      );
      fire();
    } catch (e: any) {
      alert(`Image upload failed: ${e?.message ?? 'unknown error'}`);
    } finally {
      setUploadingImage(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const insertLink = () => {
    const url = window.prompt('Link URL (e.g. https://...)');
    if (!url) return;
    exec('createLink', url);
  };

  const setColor = (color: string) => {
    exec('foreColor', color);
    setShowColorPicker(false);
  };

  // Note: changing the heading wraps the current block. We use <p> as default.
  const setBlock = (tag: string) => exec('formatBlock', `<${tag}>`);

  const COLORS = [
    '#2b2b2b', '#1e4d3a', '#7a9b7e', '#c4533a', '#c4793a',
    '#3a73c4', '#7a3ac4', '#c43a93', '#9a9a9a',
  ];

  // Paste handler — sanitize to plain text by default so people don't paste in styled Word/Outlook content.
  // They can still re-apply formatting via the toolbar.
  const onPaste = (e: React.ClipboardEvent<HTMLDivElement>) => {
    // Allow image paste (e.g. from a screenshot tool) — convert to upload
    const items = Array.from(e.clipboardData.items || []);
    const imageItem = items.find(it => it.type.startsWith('image/'));
    if (imageItem) {
      e.preventDefault();
      const file = imageItem.getAsFile();
      if (file) handleImagePick(file);
      return;
    }
    // Otherwise, paste as plain text
    const text = e.clipboardData.getData('text/plain');
    if (text) {
      e.preventDefault();
      document.execCommand('insertText', false, text);
      fire();
    }
  };

  const ToolbarBtn: React.FC<{
    onClick: (e?: React.MouseEvent) => void;
    title: string;
    children: React.ReactNode;
    active?: boolean;
  }> = ({ onClick, title, children, active }) => (
    <button
      type="button"
      title={title}
      onMouseDown={e => e.preventDefault()}  // prevent losing selection
      onClick={onClick}
      style={{
        background: active ? `${C.primary}20` : 'transparent',
        border: `1px solid ${active ? C.primary : 'transparent'}`,
        color: active ? C.primary : C.text,
        borderRadius: 4, padding: '4px 7px', cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        height: 28, minWidth: 28,
      }}
      onMouseEnter={e => { if (!active) e.currentTarget.style.background = C.bg; }}
      onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
    >{children}</button>
  );

  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 6, background: C.surface }}>
      {/* Toolbar */}
      <div style={{
        borderBottom: `1px solid ${C.border}`, padding: 6,
        display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center',
        background: C.bg, borderRadius: '6px 6px 0 0',
      }}>
        <ToolbarBtn title="Bold (Ctrl+B)" onClick={() => exec('bold')}><Bold size={14} /></ToolbarBtn>
        <ToolbarBtn title="Italic (Ctrl+I)" onClick={() => exec('italic')}><Italic size={14} /></ToolbarBtn>
        <ToolbarBtn title="Underline (Ctrl+U)" onClick={() => exec('underline')}><Underline size={14} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Heading 1" onClick={() => setBlock('h2')}><Heading1 size={14} /></ToolbarBtn>
        <ToolbarBtn title="Heading 2" onClick={() => setBlock('h3')}><Heading2 size={14} /></ToolbarBtn>
        <ToolbarBtn title="Paragraph" onClick={() => setBlock('p')}><Type size={14} /></ToolbarBtn>
        <Divider />
        <ToolbarBtn title="Bullet List" onClick={() => exec('insertUnorderedList')}><List size={14} /></ToolbarBtn>
        <ToolbarBtn title="Numbered List" onClick={() => exec('insertOrderedList')}><ListOrdered size={14} /></ToolbarBtn>
        <Divider />
        <div style={{ position: 'relative' }}>
          <ToolbarBtn title="Text Color" onClick={() => setShowColorPicker(v => !v)}>
            <div style={{
              display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
            }}>
              <span style={{ fontSize: 11, fontWeight: 700, lineHeight: 1 }}>A</span>
              <div style={{ width: 12, height: 3, background: '#c4533a' }} />
            </div>
          </ToolbarBtn>
          {showColorPicker && (
            <div style={{
              position: 'absolute', top: 32, left: 0, zIndex: 10,
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 6,
              padding: 8, display: 'grid', gridTemplateColumns: 'repeat(5, 22px)', gap: 4,
              boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
            }}>
              {COLORS.map(c => (
                <button
                  key={c}
                  type="button"
                  onMouseDown={e => e.preventDefault()}
                  onClick={() => setColor(c)}
                  title={c}
                  style={{
                    width: 22, height: 22, background: c, border: '1px solid rgba(0,0,0,0.1)',
                    borderRadius: 3, cursor: 'pointer', padding: 0,
                  }}
                />
              ))}
            </div>
          )}
        </div>
        <Divider />
        <ToolbarBtn title="Link" onClick={insertLink}><LinkIcon size={14} /></ToolbarBtn>
        <ToolbarBtn
          title={uploadingImage ? 'Uploading…' : 'Insert Image / Screenshot'}
          onClick={() => fileInputRef.current?.click()}
        >
          <ImageIcon size={14} />
          {uploadingImage && <span style={{ marginLeft: 4, fontSize: 11 }}>…</span>}
        </ToolbarBtn>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/gif,image/webp"
          style={{ display: 'none' }}
          onChange={e => {
            const f = e.target.files?.[0];
            if (f) handleImagePick(f);
          }}
        />
        <Divider />
        <ToolbarBtn title="Clear Formatting" onClick={() => exec('removeFormat')}>
          <Eraser size={14} />
        </ToolbarBtn>
        <div style={{ flex: 1 }} />
        <div style={{ fontSize: 10, color: C.textMuted, padding: '0 6px' }}>
          Tip: paste a screenshot directly with Ctrl+V
        </div>
      </div>

      {/* Editor surface */}
      <div
        ref={editorRef}
        contentEditable
        suppressContentEditableWarning
        onInput={fire}
        onPaste={onPaste}
        spellCheck
        style={{
          minHeight: 280, maxHeight: 500, overflowY: 'auto',
          padding: 14, fontSize: 14, lineHeight: 1.6, color: C.text,
          outline: 'none', background: C.surface,
        }}
      />
    </div>
  );
};

const Divider: React.FC = () => (
  <div style={{ width: 1, height: 18, background: C.border, margin: '0 4px' }} />
);

function escapeHtml(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
function escapeAttr(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
}
